// Motion helpers for tool steps and action cards (product feedback 2026-09-05: "smooth animation
// for tools when calling and succeeding"). The visibility DECISION stays in ChatSession
// (`isCardVisible`); this layer only delays unmounting so a finished step can show its ✓ and
// collapse instead of vanishing. Honours `prefers-reduced-motion` (immediate unmount, no
// animations — see index.css).
import { useEffect, useRef, useState, type ReactNode } from 'react';

/** Enter: fade + slide of a new row / card. */
export const ENTER_MS = 180;
/** Success pop of the ✓ / ! dot. */
export const POP_MS = 220;
/** How long a finished step stays visible (with its ✓) before collapsing. */
export const HOLD_MS = 700;
/** How long a step that FAILED stays, before collapsing like any other. Longer than a success because it
 *  carries a message to read — but short: the agent is usually already retrying underneath it, and a pane holding
 *  failures for seconds made a turn that went well look broken (2026-10-08). A refusal the agent fixes itself
 *  (`retried`) is not a failure at all and leaves like a success. */
export const ERROR_HOLD_MS = 1600;
/** Collapse (height + opacity → 0) before unmount. */
export const LEAVE_MS = 260;
/** Delay before a thinking / activity spinner fades in (avoids flashes between fast events). */
export const ACTIVITY_DELAY_MS = 150;
/** Enter of the user's own message: rises from the composer as it is sent. */
export const SEND_MS = 220;
/** Streamed text: each frame reveals this fraction of what has arrived and is not shown yet, so a burst of tokens
 *  drains in about this many frames (~130 ms) instead of landing as one block. */
export const REVEAL_FRAMES = 8;

export function prefersReducedMotion(): boolean {
  try {
    return typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

/** Lifecycle of a rendered item: shown normally, showing its final state before leaving, collapsing. */
export type Phase = 'present' | 'settling' | 'leaving';

export interface Departing<T> {
  item: T;
  phase: Phase;
}

/**
 * Keep items mounted for a settle + leave transition after they stop being visible.
 * `items` is the full list (still containing the hidden ones — ChatSession keeps every card);
 * `isVisible` is the hiding decision. Order follows `items`. Items that were never visible while
 * mounted (e.g. a reloaded history of finished steps) are not rendered at all.
 * `holdFor` gives one item a longer look than `HOLD_MS` before it collapses.
 */
export function useDeparting<T>(items: T[], keyOf: (item: T) => string, isVisible: (item: T) => boolean,
                                holdFor?: (item: T) => number): Departing<T>[] {
  const [phases, setPhases] = useState<Record<string, Phase>>({});
  const phasesRef = useRef(phases);
  phasesRef.current = phases;
  const snapshots = useRef<Record<string, T>>({});
  const timers = useRef<Record<string, number[]>>({});

  const clearTimers = (key: string) => {
    for (const id of timers.current[key] ?? []) window.clearTimeout(id);
    delete timers.current[key];
  };

  useEffect(() => {
    const reduced = prefersReducedMotion();
    const next: Record<string, Phase> = { ...phasesRef.current };
    let changed = false;
    const seen = new Set<string>();
    for (const item of items) {
      const key = keyOf(item);
      seen.add(key);
      if (isVisible(item)) {
        snapshots.current[key] = item;
        if (next[key] !== 'present') {
          clearTimers(key);
          next[key] = 'present';
          changed = true;
        }
      } else if (next[key] === 'present') {
        changed = true;
        if (reduced) {
          delete next[key];
          continue;
        }
        snapshots.current[key] = item; // final (done / cancelled / error) props drive the ✓ / ✕ visual
        next[key] = 'settling';
        const hold = holdFor?.(item) ?? HOLD_MS;
        const toLeaving = window.setTimeout(() => setPhases((p) => (p[key] === 'settling' ? { ...p, [key]: 'leaving' } : p)), hold);
        const toGone = window.setTimeout(
          () =>
            setPhases((p) => {
              if (!(key in p)) return p;
              const q = { ...p };
              delete q[key];
              return q;
            }),
          hold + LEAVE_MS,
        );
        timers.current[key] = [toLeaving, toGone];
      }
    }
    for (const key of Object.keys(next)) {
      if (!seen.has(key)) {
        clearTimers(key);
        delete next[key];
        delete snapshots.current[key];
        changed = true;
      }
    }
    if (changed) setPhases(next);
  }, [items, keyOf, isVisible, holdFor]);

  useEffect(() => {
    const all = timers.current;
    return () => {
      for (const key of Object.keys(all)) for (const id of all[key]) window.clearTimeout(id);
    };
  }, []);

  const out: Departing<T>[] = [];
  for (const item of items) {
    const key = keyOf(item);
    if (isVisible(item)) out.push({ item, phase: 'present' });
    else {
      const phase = phases[key];
      // Hidden but the effect has not run yet → show the settling state right away (no flash of absence).
      if (phase === 'present') out.push({ item, phase: 'settling' });
      else if (phase === 'settling' || phase === 'leaving') out.push({ item: snapshots.current[key] ?? item, phase });
    }
  }
  return out;
}

/** Height + opacity collapse (CSS grid 1fr → 0fr) used while a row or card leaves. */
export function Collapse({ leaving, className = '', children }: { leaving: boolean; className?: string; children: ReactNode }) {
  return (
    <div
      data-leaving={leaving ? 'true' : undefined}
      className={`grid transition-[grid-template-rows,opacity] duration-260 ease-in motion-reduce:transition-none ${leaving ? 'grid-rows-[0fr] opacity-0' : 'grid-rows-[1fr] opacity-100'} ${className}`}
    >
      <div className="min-h-0 overflow-hidden">{children}</div>
    </div>
  );
}

/** Renders children invisible for `delay` (ACTIVITY_DELAY_MS by default), then fades them in (layout reserved,
 *  no jump). */
export function Delayed({ children, className = '', delay = ACTIVITY_DELAY_MS }: { children: ReactNode; className?: string; delay?: number }) {
  const [shown, setShown] = useState(prefersReducedMotion());
  useEffect(() => {
    if (shown) return;
    const id = window.setTimeout(() => setShown(true), delay);
    return () => window.clearTimeout(id);
  }, [shown, delay]);
  return <span className={`transition-opacity duration-150 motion-reduce:transition-none ${shown ? 'opacity-100' : 'opacity-0'} ${className}`}>{children}</span>;
}

/** Keep the previously rendered `node` for LEAVE_MS after `active` turns false (collapse-out of a card body). */
export function useLingering(active: boolean, node: ReactNode): ReactNode | null {
  const last = useRef<ReactNode>(null);
  const [lingering, setLingering] = useState<ReactNode | null>(null);
  useEffect(() => {
    if (active) {
      last.current = node;
      setLingering(null);
      return;
    }
    if (last.current === null || prefersReducedMotion()) return;
    setLingering(last.current);
    last.current = null;
    const id = window.setTimeout(() => setLingering(null), LEAVE_MS);
    return () => window.clearTimeout(id);
  }, [active, node]);
  return active ? null : lingering;
}

/**
 * The part of `text` to show while it streams: what has arrived is revealed over a few frames (REVEAL_FRAMES)
 * rather than in the network's bursts, stopping at a word boundary when one is near so a word does not appear
 * letter by letter. Not `active` (the turn stopped streaming, a reloaded turn) or reduced motion: all of it.
 */
export function useSmoothText(text: string, active: boolean): string {
  const smooth = active && !prefersReducedMotion() && typeof requestAnimationFrame === 'function';
  const [shown, setShown] = useState(() => (smooth ? 0 : text.length));
  const latest = useRef(text);
  latest.current = text;

  useEffect(() => {
    if (!smooth) {
      setShown(latest.current.length);
      return;
    }
    let frame = 0;
    const tick = () => {
      setShown((cur) => {
        const full = latest.current;
        if (cur >= full.length) return cur;
        let next = Math.min(full.length, cur + Math.max(2, Math.ceil((full.length - cur) / REVEAL_FRAMES)));
        const gap = full.slice(next, next + 12).search(/\s/);
        if (gap >= 0) next += gap;
        return next;
      });
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [smooth]);

  return smooth ? text.slice(0, shown) : text;
}
